import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
  CheckboxCustomEvent,
  IonicModule,
  ModalController,
  RadioGroupCustomEvent,
  SearchbarCustomEvent,
} from '@ionic/angular';

@Component({
  selector: 'app-modal-select',
  templateUrl: './modal-select.component.html',
  styleUrls: ['./modal-select.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule],
})
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ModalSelectComponent<T extends Record<string, any>> implements OnInit {
  /**
   * The unique identifier for the modal select component.
   */
  @Input({ required: true }) modalId!: string;
  /**
   * Indicates whether multiple items can be selected.
   */
  @Input() multiSelect = false;
  /**
   * The title to be displayed in the modal.
   */
  @Input({ required: true }) title!: string;
  /**
   * An array of items to be displayed in the select modal.
   */
  @Input({ required: true }) items!: T[];
  /**
   * **Note:**
   * Make sure this id is at the top level of the object.
   *
   * Computes the identity of items in an iterable based on their id.
   * If an object lacks an id, use `keyText` as a default.
   */
  @Input({ required: true }) idKey!: string;
  /**
   * The key used to retrieve the text to be displayed in the modal select component.
   */
  @Input({ required: true }) textKey!: string;

  /**
   * The key used to retrieve the text to be display in the modal underneath the text key.
   */
  @Input() descriptionKey?: string;
  /**
   * An array of selected items (values) represented as strings.
   * This input property is used to pass the selected items to the component.
   */
  @Input() selected: T[] | T | null = null;

  /**
   * An array that holds the currently selected items.
   */
  currentSelected: T[] = [];
  /**
   * The selected item when `multiSelect` is set to `false`.
   */
  singleSelected: T | null = null;
  /**
   * An array of filtered items of type `T`.
   * This array holds the items that match the filtering criteria.
   */
  filteredItems: T[] = [];

  /**
   * A flag that indicates whether the confirm button should be hidden.
   */
  hideConfirmButton = true;

  constructor(private modalCtrl: ModalController) {}

  ngOnInit() {
    this.filteredItems = [...this.items];

    if (this.selected) {
      if (!this.multiSelect) {
        this.singleSelected = this.selected as T;
      } else {
        this.currentSelected = [...(this.selected as T[])];
      }
    }
  }

  /**
   * Retrieves the text value from an item based on the specified text key.
   *
   * @see {@link getItem}
   */
  getText(item: T) {
    return this.getItem(item, this.textKey);
  }

  /**
   * Retrieves the text value from an item based on the specified text key.
   *
   * @see {@link getItem}
   */
  getDescription(item: T) {
    if (!this.descriptionKey) {
      return;
    }

    return this.getItem(item, this.descriptionKey);
  }

  /**
   * Retrieves the value of a specified key from an item. If the key is not found at the top level,
   * it recursively searches nested objects.
   *
   * @param {T} item - The item from which to retrieve the value.
   * @param {string} itemKey - The key whose value needs to be retrieved.
   */
  private getItem(item: T, itemKey: string) {
    let val = '';

    Object.keys(item).some((key) => {
      if (key === itemKey) {
        val = item[key];
        return true;
      }

      if (item[key] && typeof item[key] === 'object') {
        val = this.getText(item[key]);
        return val.length > 0;
      }

      return false;
    });

    return val;
  }

  async onCancel() {
    await this.modalCtrl.dismiss(null, 'cancel', this.modalId);
  }

  async onConfirm() {
    const data = this.multiSelect ? this.currentSelected : this.singleSelected;

    await this.modalCtrl.dismiss(data, 'confirm', this.modalId);
  }

  onSearchInput(event: SearchbarCustomEvent) {
    this.filterItems(event.detail.value ?? '');
  }

  onSearchClear() {
    this.filterItems('');
  }

  compareWith(o1: T, o2: T) {
    return o1 === o2;
  }

  /**
   * Checks if the given value is included in the current selection.
   *
   * **Note:** This method is used with multi-select checkboxes.
   *
   * @param value - The value to check for inclusion in the current selection.
   */
  isChecked(value: T) {
    return this.currentSelected.find((item) => item[this.idKey] === value[this.idKey]);
  }

  /**
   * Handles the change event for a checkbox.
   *
   * **Note:** This method is used with multi-select checkboxes.
   *
   * @param event - The custom event triggered by the checkbox change.
   */
  onCheckboxChange(event: CheckboxCustomEvent) {
    const { value, checked }: { value: T; checked: boolean } = event.detail;

    if (checked) {
      this.currentSelected.push(value);
    } else {
      this.currentSelected = this.currentSelected.filter(
        (item) => item[this.idKey] !== value[this.idKey]
      );
    }

    if (
      // compare 2 objects, currentSelected with selected
      this.currentSelected?.length === (this.selected as T[])?.length &&
      this.currentSelected.every((item) =>
        (this.selected as T[]).find((s) => s[this.idKey] === item[this.idKey])
      )
    ) {
      // hide if no changes
      this.hideConfirmButton = true;
    } else {
      this.hideConfirmButton = false;
    }
  }

  /**
   * Handles the change event for the radio button group.
   *
   * @param event - The custom event triggered by the radio button group.
   */
  onRadioButtonChange(event: RadioGroupCustomEvent) {
    const { value }: { value: T } = event.detail;

    this.singleSelected = value;

    if (this.selected === this.singleSelected) {
      this.hideConfirmButton = true;
    } else {
      this.hideConfirmButton = false;
    }
  }

  /**
   * Filters the items based on the provided search query.
   * If no search query is provided, all items are included in the filtered list.
   *
   * @param searchQuery - The query string to filter items by. If omitted, all items are included.
   */
  filterItems(searchQuery?: string) {
    if (!searchQuery) {
      this.filteredItems = [...this.items];
      return;
    }

    this.filteredItems = this.items.filter((item) => {
      const text = this.getText(item).toLowerCase();
      const query = searchQuery.toLowerCase();
      const matchesText = text.includes(query);

      if (this.descriptionKey) {
        const description = this.getDescription(item)?.toLowerCase() || '';

        return matchesText || description.includes(query);
      }

      return matchesText;
    });
  }
}
